/**
 * De un fallo cualquiera al texto que ve la persona.
 *
 * Un `code` conocido tiene su clave en el catálogo. Uno desconocido —una
 * versión nueva del servidor contra una web vieja— cae en el mensaje
 * genérico; lo que no puede pasar nunca es que salga el inglés del servidor
 * en la pantalla.
 *
 * LO QUE CAMBIÓ, Y POR QUÉ. Antes esto tiraba los `fieldErrors` a la basura
 * y toda validación fallida se veía igual: «Hay datos que no están bien.
 * Revisá los campos marcados» — sin ningún campo marcado, porque solo algunos
 * formularios los leen. Javier se topó con eso agregando un patrocinador: el
 * servidor le estaba diciendo exactamente qué pasaba —«El enlace tiene que
 * empezar con https://»— y la pantalla se lo ocultaba. Un mensaje que existe,
 * es correcto y no se muestra es peor que no tenerlo: cuesta lo mismo y
 * además hace creer que el sistema no sabe qué falló.
 *
 * EL PRECIO, dicho de frente: esos mensajes los escribe el servidor y hoy
 * están SOLO EN ESPAÑOL. En las otras ocho traducciones aparece una frase en
 * español en medio de una pantalla en su idioma. Se elige igual, porque un
 * mensaje preciso que se entiende a medias sirve más que uno genérico que no
 * sirve en ningún idioma. Cuando el servidor mande mensajes traducidos, esto
 * sigue funcionando sin tocarlo.
 */
import type { TFunction } from 'i18next';
import { ApiError } from './api';

/**
 * El primer mensaje por campo que mandó el servidor, si mandó alguno.
 *
 * EL PRIMERO Y NO TODOS: cuatro quejas juntas sobre el mismo formulario no
 * ayudan a arreglarlo. La persona corrige lo primero y vuelve a enviar, y
 * ahí aparece lo siguiente si todavía queda algo.
 */
export function firstFieldMessage(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.fieldErrors === undefined) return null;
  for (const mensaje of Object.values(error.fieldErrors)) {
    if (typeof mensaje === 'string' && mensaje.trim() !== '') return mensaje;
  }
  return null;
}

/**
 * El resumen: SIN mirar los campos.
 *
 * Es para los formularios que YA pintan cada mensaje al lado de su campo
 * —el registro, el perfil, crear club—. Ahí, poner el mismo texto arriba en
 * rojo lo dice dos veces, y quien lo lee dos veces se pregunta si son dos
 * problemas distintos.
 */
export function errorSummary(t: TFunction, error: unknown): string {
  const code = error instanceof ApiError ? error.code : 'UNKNOWN';
  const key = `error.${code}`;
  const translated = t(key, { defaultValue: '' });
  return translated === '' ? t('error.UNKNOWN') : translated;
}

/**
 * El mensaje para mostrar en un solo lugar.
 *
 * Prefiere el del campo cuando el servidor mandó uno: es el que dice qué
 * pasó. Para los formularios que pintan los campos por su cuenta está
 * `errorSummary`.
 */
export function errorMessage(t: TFunction, error: unknown): string {
  return firstFieldMessage(error) ?? errorSummary(t, error);
}
